import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { CustomModal } from './ScenarioColumnEditModal';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Spin, Switch, Tooltip } from 'antd';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import SearchBox from 'components/molecules/SearchBox';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { useFetchGetAllProjects } from 'hooks/pricingSimulator/useFetchGetAllProjects';
import { FilterContextData } from 'contexts/FilterContext';
import { getAllProjectsStateType } from 'components/organism/PricingSimulator/Projects';
import { UserContext } from 'contexts/ConfigContext';
import { compareUsers, generateErrorMsg, getConfig, sendNotification } from 'utils/helperMethods';
import { pricingAnalyticsUrls, pricingSimulatorUrls } from 'config/urls';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { pricingAnalyticsBaseScenarioTableColumns } from 'utils/getPricingSimulatorTableColumns';
import { queryClient } from 'services/reactQueryService';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { getAllScenarioParser } from 'parser/pricingSimulator/ProjectsScenariosParser';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useNavigate } from 'react-router-dom';
import {
    PricingAnalyticsNotificationMessages,
    PricingSimulatorNotificationMessages,
    PricingSimulatorStatusType,
} from 'utils/Enums';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

type Props = {
    open: boolean;
    onCancel: Function;
    onSubmitClick: Function;
    from: 'create' | 'edit';
    mode: 'comparison' | 'analytics';
};

const defaultTableQueryVariables: getAllProjectsStateType = {
    search_keyword: '',
    limit: 50,
    offset: 0,
    sort_by: 'updated_at',
    sorting_order: 'desc',
    user_id: localStorage.getItem('email') ?? '',
    project_scope: 'all',
};

const BaseScenarioSelectionModal: FC<Props> = ({
    open,
    onSubmitClick,
    onCancel,
    from = 'create',
    mode = 'analytics',
}) => {
    const [search, setSearch] = useState('');
    const { appConfig } = useContext(UserContext);
    const pricingSimulator = getConfig('pricingSimulator', appConfig);
    const limit = pricingSimulator?.indexPageTableLimit;
    const analyticsBaseScenarioFixedColumns = pricingSimulator.analyticsBaseScenarioFixedColumns;
    const projectStatuses = pricingSimulator.projectStatuses;
    const enablePrivatePublicToggle = pricingSimulator.enablePrivatePublicToggle;

    //simulator wrapper context
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const dim1Attribute = dimensionConfigData?.dim1Attribute;

    const [tableProjectsReactQueryVariables, setTableProjectsReactQueryVariables] = useState<getAllProjectsStateType>({
        ...defaultTableQueryVariables,
    });
    const [allScenariosTableVariables, setAllScenariosTableVariables] = useState<any>();
    const [expandedKeys, setExpandedKeys] = useState<any>({});
    const [selectedScenarioDetails, setSelectedScenarioDetails] = useState<
        | {
              id: number;
              createdBy?: string;
              name: string;
              status: PricingSimulatorStatusType;
              isPrivate: boolean;
          }[]
        | null
    >(null);
    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);
    const { selectedReportInfo, updateReportProperty, updateComparisonProperty, scenarioComparisonState } =
        useContext(PricingAnalyticsDataContext);
    const enabledVariable =
        open &&
        Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        );
    //get all projects
    const {
        data: allProjectsData,
        loading: allProjectsDataLoading,
        error: allProjectsDataError,
        count: allProjectsDataCount,
        setData: setAllProjectsData,
    } = useFetchGetAllProjects({
        method: 'post',
        queryConfig: {
            enabled: Boolean(dim1Attribute) && Boolean(enabledVariable) && Boolean(tableProjectsReactQueryVariables),
        },
        queryKey: ['ps-analytics-table-projects', tableProjectsReactQueryVariables],
        url: `${pricingSimulatorUrls.getAllProjectsUrl}`,
        variables: {
            ...tableProjectsReactQueryVariables,
        },
        extras: {
            newlyCreatedProjectId: null,
            setNewlyCreatedProjectId: () => {},
            setExpandedKeys,
            setAllScenariosTableVariables,
        },
    });

    //get all scenarios for a particular project id
    const {} = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(allScenariosTableVariables?.projectId) },
        queryKey: ['ps-analytics-table-projects', 'ps-analytics-table-scenarios', allScenariosTableVariables],
        // if my scenarios enabled add the query string to the url
        url: `${pricingSimulatorUrls.getAllScenariosUrl}/${allScenariosTableVariables?.projectId}/scenarios${
            tableProjectsReactQueryVariables.user_id && tableProjectsReactQueryVariables.project_scope === 'self'
                ? '?user_id=' + tableProjectsReactQueryVariables.user_id
                : ''
        }`,
        variables: {
            ...allScenariosTableVariables,
        },
        parserFunction: (res: any) => {
            //if error, remove loader and add error message
            if (res?.error) {
                const updatedData = allProjectsData?.map((project: any) => {
                    //find the project
                    if (project?.projectId === allScenariosTableVariables?.projectId) {
                        return {
                            ...project,
                            children: [{ isChild: true, errorMessage: 'No Data' }],
                        };
                    }
                    return {
                        ...project,
                    };
                });
                setAllProjectsData(updatedData);
                return {};
            }
            try {
                if (res?.length === 0) {
                    throw 'No Data';
                }
                //scenarios data is added in all projects data inside children key as its nested
                const updatedData = getAllScenarioParser(res, allProjectsData);
                if (updatedData?.length) setAllProjectsData(updatedData);
                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    const memoisedColumns = useMemo(() => {
        return pricingAnalyticsBaseScenarioTableColumns(
            analyticsBaseScenarioFixedColumns,
            {
                sortBy: tableProjectsReactQueryVariables?.sort_by || 0,
                sortOrder:
                    tableProjectsReactQueryVariables?.sorting_order === 'asc'
                        ? 'ascend'
                        : tableProjectsReactQueryVariables?.sorting_order === 'desc'
                        ? 'descend'
                        : null,
            },
            selectedScenarioDetails?.map((item) => item.id) ?? [],
            projectStatuses,
            enablePrivatePublicToggle,
        );
    }, [allProjectsData, selectedScenarioDetails]);

    const commonLoading = allProjectsDataLoading;

    function infiniteScrollOnNext(loading: boolean) {
        if (!loading) {
            const newOffset = tableProjectsReactQueryVariables.offset + tableProjectsReactQueryVariables.limit;
            setTableProjectsReactQueryVariables({ ...tableProjectsReactQueryVariables, offset: newOffset });
        }
    }
    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        setTableProjectsReactQueryVariables({ ...tableProjectsReactQueryVariables, offset: 0, search_keyword: val });
    };
    //set expanded keys to default state when user searches
    useEffect(() => {
        setExpandedKeys({});
    }, [tableProjectsReactQueryVariables?.search_keyword, tableProjectsReactQueryVariables?.user_id]);

    //reset all fields once popup is closed
    useEffect(() => {
        if (!open) {
            setTableProjectsReactQueryVariables({ ...defaultTableQueryVariables, limit });
            setExpandedKeys({});
            setSearch('');
            setAllScenariosTableVariables(undefined);
            setSelectedScenarioDetails(null);
            //removing query from cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['ps-analytics-table-projects'],
            });
        }
        if (open && mode === 'comparison') {
            const tempArray =
                scenarioComparisonState?.selectedScenarios?.map((item) => {
                    return {
                        id: item?.scenarioId,
                        name: item?.scenarioName,
                        status: item?.status,
                        isPrivate: item?.isPrivate,
                    };
                }) ?? [];
            setSelectedScenarioDetails(
                selectedReportInfo?.baseScenario
                    ? tempArray?.length > 0
                        ? tempArray
                        : [
                              {
                                  id: selectedReportInfo?.baseScenario?.scenarioId,
                                  name: selectedReportInfo?.baseScenario?.scenarioName,
                                  status: selectedReportInfo?.baseScenario?.status,
                                  isPrivate: selectedReportInfo?.baseScenario?.isPrivate,
                              },
                          ]
                    : null,
            );
        }
    }, [open]);

    const navigate = useNavigate();

    //mutate function
    const { mutate: addScenarioMutateFn, loading: addScenarioLoading } = useCommonMutationHandler({
        url: pricingAnalyticsUrls.addBaseScenarioUrl,
        method: 'post',
    });

    //update report name api
    const { mutate: updateScenarioListMutate, loading: updateScenarioListLoading } = useCommonMutationHandler({
        url: pricingAnalyticsUrls.updateReportScenarioList,
        method: 'post',
    });

    const updateScenarioListHandler = () => {
        const items = selectedScenarioDetails?.map((item) => {
            return {
                scenarioId: Number(item?.id),
                scenarioName: String(item?.name),
                status: item?.status ?? 'DRAFT',
                isPrivate: item?.isPrivate ?? false,
            };
        });

        const newList =
            items?.map((i) => i.scenarioId)?.filter((s) => s !== selectedReportInfo?.baseScenario.scenarioId) || [];
        const payload = {
            report_id: selectedReportInfo?.reportId,
            scenario_ids: newList,
            user_name: localStorage.getItem('email') ?? '',
        };

        if (newList?.length > 0) {
            updateScenarioListMutate(
                { ...payload },
                {
                    onSuccess: (data) => {},
                    onError: (err: any) => {
                        sendNotification('error', err.response?.data?.detail);
                    },
                },
            );
        }
        // ideally should be inside onSuccess
        updateComparisonProperty('selectedScenarios', items ?? []);

        onCancel();
    };

    return (
        <CustomModal
            open={open}
            title={mode === 'comparison' ? 'Select Scenario' : 'Select Base Scenario'}
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{ disabled: !selectedScenarioDetails?.[0]?.id, style: { borderRadius: 6 } }}
            onOk={() => {
                if (addScenarioLoading) return;
                if (mode === 'comparison') {
                    updateScenarioListHandler();
                } else {
                    const payload = {
                        base_scenario_id: selectedScenarioDetails?.[0]?.id,
                        created_by: localStorage.getItem('email') || '',
                        report_id: from === 'create' ? null : selectedReportInfo?.reportId,
                    };
                    addScenarioMutateFn(payload, {
                        onSuccess: (data) => {
                            sendNotification('success', PricingAnalyticsNotificationMessages.BASE_SCENARIO_SUCCESS);
                            onCancel(); //remove popup
                            if (from === 'create') {
                                navigate(`/pricing/recommendation/pricing-analytics/update/${data?.data?.report_id}`);
                            } else {
                                updateReportProperty('baseScenario', {
                                    scenarioId: Number(selectedScenarioDetails?.[0]?.id),
                                    scenarioName: String(selectedScenarioDetails?.[0]?.name),
                                    status: selectedScenarioDetails?.[0]?.status ?? 'DRAFT',
                                    isPrivate: selectedScenarioDetails?.[0]?.isPrivate ?? false,
                                });

                                //  if base scenario has changes. We need to pull the records from api call
                                queryClient.removeQueries({
                                    exact: false,
                                    queryKey: ['pa-comparison-details'],
                                });
                            }
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail),
                                PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                            console.log(error, 'logging error');
                        },
                    });
                }

                onSubmitClick();
            }}
            okText={addScenarioLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : 'Add'}
            destroyOnClose
            centered
            width={750}
            onCancel={(e) => {
                if (addScenarioLoading) return;
                onCancel(e);
            }}
        >
            {mode === 'comparison' && (
                <div className="flex items-center gap-2 mb-2 text-redAlt font-xs">
                    <ExclamationCircleOutlined />
                    <span>Select upto 5 scenarios</span>
                </div>
            )}
            <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                    <div className="flex gap-2 items-center">
                        <label className="text-promoColour font-xxs">My Scenario</label>
                        <Switch
                            checked={tableProjectsReactQueryVariables.project_scope === 'self'}
                            onChange={(val) => {
                                setTableProjectsReactQueryVariables({
                                    ...tableProjectsReactQueryVariables,
                                    offset: 0,
                                    project_scope: val ? 'self' : 'all',
                                });
                                // queryClient.removeQueries({
                                //     exact: false,
                                //     queryKey: ['ps-index-table-projects'],
                                // });
                            }}
                        />
                    </div>
                </div>

                <SearchBox search={search} setSearch={setSearch} onSearch={onSearch} />
            </div>
            <InfiniteScroller
                data={allProjectsData}
                loading={commonLoading}
                hasMore={allProjectsData?.length < allProjectsDataCount || commonLoading}
                height={600}
                //height={200}
                //     loader={true}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setTableProjectsReactQueryVariables({
                                ...tableProjectsReactQueryVariables,
                                sort_by: sort?.column?.sortKey,
                                sorting_order: sortMapping?.[sort?.order] || 'desc',
                                offset: 0,
                            });
                            //default it to dim1 column on cancelling sorting
                        } else {
                            setTableProjectsReactQueryVariables({
                                ...tableProjectsReactQueryVariables,
                                sort_by: 'updated_at',
                                sorting_order: 'desc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky
                    // id={projectsTableId}
                    //loading={commonLoading}
                    columns={memoisedColumns}
                    scroll={{ x: '100%' }}
                    dataSource={allProjectsData}
                    pagination={false}
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: allProjectsDataError ? <ErrorBox msg={String(allProjectsDataError)} /> : '',
                    }}
                    rowClassName={(record: any) => {
                        let className = '';

                        //if expired, then disabled row
                        if (record?.status === 'expired') {
                            className += 'opacity-60 cursor-not-allowed pointer-events-none';
                        } else {
                            if (record?.key in expandedKeys) className += ' bg-backgroundGreyAlt';
                            if (record && 'children' in record) className += ' cursor-pointer';
                        }
                        return className;
                    }}
                    expandable={{
                        expandedRowKeys: Object.keys(expandedKeys),
                        expandRowByClick: true,
                        onExpand(expanded, record: any) {
                            if (record && 'scenarioId' in record) {
                                const item = {
                                    id: record?.scenarioId,
                                    createdBy: record?.createdBy,
                                    name: record?.projectName,
                                    status: record?.status,
                                    isPrivate: record?.isPrivate,
                                };
                                let tempSelectedScenarios: any[] = [];
                                if (mode === 'analytics') {
                                    tempSelectedScenarios = [item];
                                } else {
                                    //check if index =0, base scenario will be indexed to 0 and it cant be deselected
                                    if (selectedScenarioDetails?.[0]?.id === record?.scenarioId) return;
                                    const itemIndex =
                                        selectedScenarioDetails?.findIndex((item) => item.id === record?.scenarioId) ??
                                        -1;
                                    //else deselect clicked item from selected list
                                    //remove selected item
                                    if (itemIndex > 0) {
                                        tempSelectedScenarios = [...(selectedScenarioDetails ?? [])];
                                        tempSelectedScenarios.splice(itemIndex, 1);
                                        //add the item
                                    } else {
                                        if (selectedScenarioDetails && selectedScenarioDetails?.length >= 5) {
                                            return sendNotification('error', 'You can select up to 5 scenarios');
                                        }
                                        tempSelectedScenarios = [...(selectedScenarioDetails ?? []), item];
                                    }
                                }
                                setSelectedScenarioDetails(tempSelectedScenarios);
                                return;
                            }
                            //if false, collapse table row
                            if (!expanded) {
                                const tempExpandedItems = { ...expandedKeys };
                                delete tempExpandedItems?.[record?.key];
                                setExpandedKeys(tempExpandedItems);
                                setAllScenariosTableVariables({});
                                //if true, expand table row
                            } else if (record && 'children' in record) {
                                setAllScenariosTableVariables({ projectId: record?.projectId });
                                setExpandedKeys({ ...expandedKeys, [record?.key]: true });
                            }
                        },
                    }}
                />
            </InfiniteScroller>
        </CustomModal>
    );
};

export default BaseScenarioSelectionModal;
